import {useContext, useEffect, useState, useRef} from "react";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {NavLink} from "react-router-dom";
import {CardElement, useStripe, useElements} from "@stripe/react-stripe-js";
import {AxiosError} from "axios";

// Assets
import {IckonicLogo} from "../../Assets/SVGs/Logos";
import IconEye from "../../Assets/SVGs/Icons/UI/Eye";
import IconEyeOff from "../../Assets/SVGs/Icons/UI/EyeOff";
import IconQuestionFilled from "../../Assets/SVGs/Icons/AlertAndInfo/QuestionFilled";

// Models
import {PlanType} from "../../Models/Enums/PlanType";
import PlanDTO from "../../Models/DTOs/Plan";
import {RegisterUserRequest, RegisterUserResponse} from "../../Models/DTOs/Account";
import {LoginDTO} from "../../Models/DTOs/LoginDTO";
import {IProgressStep} from "../../Models/Interfaces/IProgressStep";
import {AuthUser} from "../../Models/DTOs/AuthUser";

// Api
import EmailFreeViewAPI from "../../Api/EmailFreeView";
import {RegisterUser, LoginUser, GetAuthUser} from "../../Api/Account";
import {CreateSubscription} from "../../Api/Stripe";
import {GetSignupQuote, SignupQuote, signupQuoteRequest, signupQuotesMatch} from "../../Api/SignupQuote";
import {useSignupQuote} from "../../Hooks/Stripe/useSignupQuote";
import {clearAffiliateReferral, getAffiliateReferral} from "../../Helpers/AffiliateReferral";

// Components
import ButtonPill from "../../Components/Buttons/ButtonPill";
import InputField from "../../Components/Inputs/InputField";
import InputCheckbox from "../../Components/Inputs/InputCheckbox";
import InputsStripe from "../../Components/Inputs/InputsStripe";
import PlansSelector from "../../Components/Layouts/Plans/PlansSelector";
import Tooltip from "../../Components/ToolTip";
import PopUp from "../../Components/Popups/PopUp";
import ProgressPanel from "../../Components/Popups/ProgressPanel";

// Global state
import {GlobalInterfaceContext} from "../../Context/GlobalInterfaceContext";
import {UserAuthenticationContext} from "../../Context/UserAuthenticationContext";

// Utility functions
import {emailValidation, nameValidation, passwordValidation} from "../../Helpers/Inputs";
import {setFormError} from "../../Helpers/Forms";
import {SetTitle} from "../../Helpers/PageMetadata";
import {getPaymentIntentClientAction} from "../../Helpers/StripePaymentIntent";
import {createBillingAttemptId} from "../../Helpers/BillingAttempt";
import {getResolvedOffer} from "../../Helpers/BillingOffers";
import {clearPromotionCode, getPromotionCode, withPromotionCode} from "../../Helpers/PromotionCode";
import {getStripeDiscount, getStripePriceId} from "../../Helpers/Currency/Stripe";

// Constants
import {RoutePaths} from "../../Constants/RoutePaths";
import {
    createInitialSignupProgressSteps,
    getNextSignupFlowAction,
} from "./signupFlowState";

interface FormState {
    FirstName: string;
    LastName: string;
    Email: string;
    Password: string;
    ConfirmPassword: string;
    CreditCard: string;
    AllowCommercialEmails: boolean;
    TermsOfServiceAccept: boolean;
}

const initialFormState: FormState = {
    FirstName: "",
    LastName: "",
    Email: "",
    Password: "",
    ConfirmPassword: "",
    CreditCard: "",
    AllowCommercialEmails: true,
    TermsOfServiceAccept: false,
};

type FormKey = keyof FormState;

interface ErrorState {
    FirstName: string;
    LastName: string;
    Email: string;
    Password: string;
    Plans: string;
    ConfirmPassword: string;
    CreditCard: string;
    AllowCommercialEmails: string;
    TermsOfServiceAccept: string;
}

const initialErrorState: ErrorState = {
    FirstName: "valid",
    LastName: "valid",
    Email: "valid",
    Password: "valid",
    Plans: "valid",
    ConfirmPassword: "valid",
    CreditCard: "valid",
    AllowCommercialEmails: "valid",
    TermsOfServiceAccept: "valid",
};

const SignUp = () => {
    // Abort controller
    const
        abortRef = useRef<AbortController | null>(null),
        getAbort = () => {
            abortRef.current?.abort();
            abortRef.current = new AbortController();
            return abortRef.current;
        },

        // Global state
        globalInterfaceContext = useContext(GlobalInterfaceContext),
        userAuthenticationContext = useContext(UserAuthenticationContext),

        // Hooks
        {t} = useTranslation(),
        stripe = useStripe(),
        elements = useElements(),

        // Local state
        [formData, setFormData] = useState<FormState>(initialFormState),
        [zipCode, setZipCode] = useState(""),
        [errors, setErrors] = useState<ErrorState>(initialErrorState),
        [selectedPlan, setSelectedPlan] = useState<PlanDTO | null>(null),
        [isCardComplete, setCardComplete] = useState(false),
        [plansPopUpActive, setPlansPopUpActive] = useState(false),
        [isLoading, setIsLoading] = useState(false),
        [progressPopUpActive, setProgressPopUpActive] = useState(false),
        [progressMessage, setProgressMessage] = useState<string>(""),
        [progressSteps, setProgressSteps] = useState<IProgressStep[]>(createInitialSignupProgressSteps),
        [canResumeBilling, setCanResumeBilling] = useState(false),
        [billingAttemptId] = useState(createBillingAttemptId),
        [affiliateReferral, setAffiliateReferral] = useState(() => getAffiliateReferral()),
        signupQuote = useSignupQuote({
            plan: selectedPlan,
            userId: userAuthenticationContext.userData?.id,
            affiliateCode: affiliateReferral?.code,
            getValidAccessToken: userAuthenticationContext.getValidAccessToken,
            enabled: !userAuthenticationContext.authInitializing,
        }),

        signupAffiliatePricing = affiliateReferral && !userAuthenticationContext.authInitializing
            ? {
                affiliateCode: userAuthenticationContext.userData?.id ? undefined : affiliateReferral.code,
                userId: userAuthenticationContext.userData?.id || undefined,
                getValidAccessToken: userAuthenticationContext.getValidAccessToken,
            }
            : undefined,

        // Refs
        emailInputRef = useRef<HTMLInputElement>(null),
        registeredUserIdRef = useRef<string | null>(null),
        authenticatedUserIdRef = useRef<string | null>(null),
        billingAttemptedRef = useRef(false),

        // Validation map
        validationFunctions: { [key: string]: (value: string, t: TFunction) => string } = {
            Email: emailValidation,
            Password: passwordValidation,
            FirstName: nameValidation,
        },

        // Handlers
        handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const {name, value, type, checked} = e.target;
            setFormData((prev) => ({
                ...prev,
                [name]: type === "checkbox" ? checked : value,
            }));
        },

        handlePlanSelect = (plan: PlanDTO) => {
            setSelectedPlan(plan);
            setFormError(setErrors, "Plans", "valid");
        },

        // Step 1: register
        registerUser = async (
            dto: RegisterUserRequest,
            ac: AbortController
        ): Promise<{ success: boolean; data?: RegisterUserResponse; error?: string }> => {
            const payload: RegisterUserRequest = {
                    email: dto.email.trim().toLowerCase(),
                    password: dto.password,
                    firstname: dto.firstname?.trim(),
                    lastname: dto.lastname?.trim(),
                    allow_commercial_emails: dto.allow_commercial_emails,
                    ...(affiliateReferral ? {
                        affiliate_code: affiliateReferral.code,
                        promotekit_referral: getAffiliateReferral()?.promotekitReferral,
                    } : {}),
                },

                result = await RegisterUser(payload, ac);

            if (result instanceof AxiosError) {
                setProgressMessage("Registration failed");
                setProgressSteps((prev) => [{percent: 25, status: "error"}, prev[1], prev[2], prev[3]]);
                return {success: false, error: result.message || "Registration failed"};
            }

            if (!result?.user_id) {
                setProgressMessage("Registration failed");
                setProgressSteps((prev) => [{percent: 25, status: "error"}, prev[1], prev[2], prev[3]]);
                return {success: false, error: "Registration failed"};
            }

            setProgressSteps((prev) => [{percent: 25, status: "success"}, prev[1], prev[2], prev[3]]);
            return {success: true, data: result};
        },

        // Step 2: login + fetch user data
        loginAndFetchUserData = async (
            email: string,
            password: string,
            ac: AbortController
        ): Promise<{ success: boolean; data?: string; error?: string }> => {
            setProgressMessage("Embedding Divine Spark");
            try {
                const loginResponse = await LoginUser(email, password, ac),
                    tokenData = loginResponse as LoginDTO;
                if (!tokenData?.access_token) throw new Error("Login failed. Please check your credentials.");
                if (!tokenData.refresh_token) throw new Error("Login failed to establish a session.");

                userAuthenticationContext.storeTokens(
                    {
                        accessToken: tokenData.access_token,
                        refreshToken: tokenData.refresh_token,
                        accessExpiresIn: tokenData.expires_in,
                        refreshExpiresIn: tokenData.refresh_expires_in,
                    },
                    true
                );

                const me = await GetAuthUser(tokenData.access_token, ac);
                if ((me as any)?.isAxiosError || !(me as AuthUser)?.id) {
                    const msg = "User data fetch failed";
                    setProgressMessage(msg);
                    setProgressSteps((prev) => [prev[0], {percent: 50, status: "error"}, prev[2], prev[3]]);
                    return {success: false, error: msg};
                }

                const verifiedUser = me as AuthUser;
                userAuthenticationContext.setUserData(verifiedUser);

                setProgressSteps((prev) => [prev[0], {percent: 50, status: "success"}, prev[2], prev[3]]);
                return {success: true, data: verifiedUser.id};
            } catch (err) {
                const msg = err instanceof Error ? err.message : "An unexpected error occurred";
                setProgressMessage(msg);
                setProgressSteps((prev) => [prev[0], {percent: 50, status: "error"}, prev[2], prev[3]]);
                return {success: false, error: msg};
            }
        },

        // Step 4: create subscription
        createSubscription = async (
            aspNetUserId: string,
            ac: AbortController,
            paymentMethodId: string,
            expectedQuote: SignupQuote,
        ): Promise<{ success: boolean; error?: string }> => {
            setProgressMessage("Entering The Matrix");

            if (!stripe) {
                const msg = "Stripe not initialized";
                setProgressSteps((prev) => [prev[0], prev[1], prev[2], {percent: 100, status: "error"}]);
                return {success: false, error: msg};
            }

            // The create-subscription endpoint is self-or-admin protected. We logged the
            // user in at step 2 (loginAndFetchUserData -> storeTokens), so a valid access
            // token now exists — attach it so the backend authorises the request.
            const accessToken = await userAuthenticationContext.getValidAccessToken();
            if (!accessToken) {
                const msg = "Session expired. Please log in and try again.";
                setProgressMessage(msg);
                setProgressSteps((prev) => [prev[0], prev[1], prev[2], {percent: 100, status: "error"}]);
                return {success: false, error: msg};
            }

            try {
                if (!selectedPlan) {
                    const em = t("error-plans-message");
                    setFormError(setErrors, "Plans", em);
                    setProgressSteps((prev) => [prev[0], prev[1], prev[2], {percent: 100, status: "error"}]);
                    return {success: false, error: em};
                }

                const
                    offer = getResolvedOffer(selectedPlan),
                    planType: PlanType = selectedPlan.period === 1 ? 1 : 0;

                // After registration, only the attribution saved on the account is authoritative.
                const confirmedQuote = await GetSignupQuote(
                    signupQuoteRequest(selectedPlan, aspNetUserId), ac, accessToken,
                );
                if (!signupQuotesMatch(expectedQuote, confirmedQuote)) {
                    signupQuote.retry();
                    throw new Error("Your price changed. Please review your first payment before continuing.");
                }

                // Only hand recovery to ChoosePlan once a billing mutation can have happened.
                billingAttemptedRef.current = true;
                setCanResumeBilling(true);
                // 1. Call API to create subscription (Passes PM ID to attach on server)
                const response = await CreateSubscription(
                    formData.Email,
                    Number(selectedPlan.planId),
                    aspNetUserId,
                    planType,
                    accessToken,
                    ac,
                    {
                        offerKey: offer?.offer_key,
                        offerId: offer?.id,
                        offerVersion: offer?.version,
                        finalAmountMinor: offer?.final_amount_minor,
                        promotionCode: offer?.bucket === "targeted" ? getPromotionCode() ?? undefined : undefined,
                        // Legacy fallback for a staggered frontend/API rollout.
                        priceId: offer || selectedPlan.offerCatalogResolved
                            ? undefined
                            : getStripePriceId(selectedPlan),
                        couponId: offer || selectedPlan.offerCatalogResolved
                            ? undefined
                            : getStripeDiscount(selectedPlan),
                        paymentMethodId: paymentMethodId,
                        attemptId: billingAttemptId,
                        affiliateExpectedAmountMinor: expectedQuote.amount_due_minor,
                    }
                );

                if (response instanceof AxiosError) {
                    const msg = "Failed to create subscription";
                    setProgressMessage(msg);
                    setProgressSteps((prev) => [prev[0], prev[1], prev[2], {percent: 100, status: "error"}]);
                    return {success: false, error: msg};
                }

                const {status, client_secret, payment_intent_status} = response;

                // 2. Scenario A: Success immediately (No 3DS required)
                if (status === 'active' || status === 'trialing') {
                    clearPromotionCode();
                    setProgressSteps((prev) => [prev[0], prev[1], prev[2], {percent: 100, status: "success"}]);
                    return {success: true};
                }

                // 3. Scenario B: 3D Secure Required (The only Popup)
                if (status === 'incomplete') {
                    const intentAction = getPaymentIntentClientAction(
                        payment_intent_status,
                        !!client_secret
                    );
                    if (intentAction === "return_to_account") {
                        window.location.assign(RoutePaths.Account);
                        return {success: false, error: "Payment is processing."};
                    }
                    if (
                        intentAction !== "confirm_existing" &&
                        intentAction !== "confirm_with_payment_method"
                    ) {
                        const msg = "Payment authentication is pending. Please continue from your account.";
                        setProgressMessage(msg);
                        setProgressSteps((prev) => [prev[0], prev[1], prev[2], {percent: 100, status: "error"}]);
                        return {success: false, error: msg};
                    }
                    if (!client_secret) {
                        const msg = "Payment authentication could not be started. Please continue from your account.";
                        setProgressMessage(msg);
                        setProgressSteps((prev) => [prev[0], prev[1], prev[2], {percent: 100, status: "error"}]);
                        return {success: false, error: msg};
                    }

                    setProgressMessage("Confirming with Bank...");

                    // Existing intents already have a PaymentMethod for action/
                    // confirmation states. A failed/declined intent explicitly
                    // requires the selected PaymentMethod to be supplied again.
                    const {error, paymentIntent} = intentAction === "confirm_with_payment_method"
                        ? await stripe.confirmCardPayment(client_secret, {payment_method: paymentMethodId})
                        : await stripe.confirmCardPayment(client_secret);

                    if (error) {
                        const msg = error.message || "Payment confirmation failed";
                        setProgressMessage(msg);
                        setProgressSteps((prev) => [prev[0], prev[1], prev[2], {percent: 100, status: "error"}]);
                        return {success: false, error: msg};
                    }

                    if (paymentIntent && paymentIntent.status === 'succeeded') {
                        // 3DS passed, subscription is now active
                        clearPromotionCode();
                        setProgressSteps((prev) => [prev[0], prev[1], prev[2], {percent: 100, status: "success"}]);
                        return {success: true};
                    }
                }

                // 4. Fallback (Unknown status or missing secret)
                const msg = "Payment incomplete. Please check your card.";
                setProgressMessage(msg);
                setProgressSteps((prev) => [prev[0], prev[1], prev[2], {percent: 100, status: "error"}]);
                return {success: false, error: msg};

            } catch (err: any) {
                const msg = err?.message || "Failed to create subscription";
                setProgressMessage(msg);
                setProgressSteps((prev) => [prev[0], prev[1], prev[2], {percent: 100, status: "error"}]);
                return {success: false, error: msg};
            }
        },

        // Kicks off customer registration -> payment method / subscription creation
        handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            if (isLoading) return;

            const nextAction = getNextSignupFlowAction({
                registeredUserId: registeredUserIdRef.current,
                authenticatedUserId: authenticatedUserIdRef.current,
                billingAttempted: billingAttemptedRef.current,
            });
            if (nextAction === "resume_billing") {
                // Reloading through the authenticated route refreshes /v1/verify and
                // lets ChoosePlan resume the existing incomplete subscription.
                window.location.assign(withPromotionCode(RoutePaths.ChoosePlan));
                return;
            }

            if (!selectedPlan) {
                setFormError(setErrors, "Plans", t("error-plans-message"));
                return;
            }
            const expectedQuote = signupQuote.quote;
            if (!expectedQuote || signupQuote.loading || signupQuote.error) {
                setFormError(setErrors, "Plans", "Please confirm your first payment before continuing.");
                return;
            }
            if (!formData.TermsOfServiceAccept) {
                setFormError(setErrors, "TermsOfServiceAccept", t("please-accept-terms") || "Please accept the terms.");
                return;
            }
            if (!isCardComplete) {
                setFormError(setErrors, "CreditCard", t("error-credit-card"));
                return;
            }

            const ac = getAbort();

            const dto: RegisterUserRequest = {
                email: formData.Email,
                password: formData.Password,
                firstname: formData.FirstName,
                lastname: formData.LastName,
                allow_commercial_emails: formData.AllowCommercialEmails,
            };

            setIsLoading(true);
            setCanResumeBilling(false);
            setProgressSteps(createInitialSignupProgressSteps());
            setProgressPopUpActive(true);
            setProgressMessage("Constructing Hologram");

            try {
                // 1. Register User
                if (!registeredUserIdRef.current) {
                    const registrationResult = await registerUser(dto, ac);
                    if (!registrationResult.success || !registrationResult.data?.user_id) return;
                    registeredUserIdRef.current = registrationResult.data.user_id;
                } else {
                    setProgressSteps((prev) => [
                        {percent: 25, status: "success"},
                        prev[1],
                        prev[2],
                        prev[3],
                    ]);
                }

                // 2. Login & Fetch Data
                if (!authenticatedUserIdRef.current) {
                    const loginResult = await loginAndFetchUserData(formData.Email, formData.Password, ac);
                    if (!loginResult.success || !loginResult.data) return;
                    authenticatedUserIdRef.current = loginResult.data;
                }
                const aspNetUserId = authenticatedUserIdRef.current;

                if (!stripe || !elements) {
                    setProgressMessage("Payment processor not initialized");
                    setProgressSteps((prev) => [prev[0], prev[1], {percent: 75, status: "error"}, prev[3]]);
                    return;
                }

                // 3. Create Payment Method Client-Side (Silent - No Popup)
                // We skip the old reserveAndSetDefaultPaymentMethod step to avoid the extra verify call.
                setProgressMessage("Reserving Passage");
                const cardElement = elements.getElement(CardElement);
                if (!cardElement) {
                    setProgressMessage("Card element not found");
                    setProgressSteps((prev) => [prev[0], prev[1], {percent: 75, status: "error"}, prev[3]]);
                    return;
                }

                const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
                    type: 'card',
                    card: cardElement,
                    billing_details: {
                        email: formData.Email,
                        name: `${formData.FirstName} ${formData.LastName}`.trim(),
                        address: zipCode ? { postal_code: zipCode } : undefined,
                    },
                });

                if (pmError || !paymentMethod) {
                    const msg = pmError?.message || "Failed to create payment method";
                    setProgressMessage(msg);
                    setProgressSteps((prev) => [prev[0], prev[1], {percent: 75, status: "error"}, prev[3]]);
                    return;
                }

                // Mark step 3 as success (PM created)
                setProgressSteps((prev) => [prev[0], prev[1], {percent: 75, status: "success"}, prev[3]]);

                // 4. Create Subscription (Triggers the only popup if needed)
                // Quote failures can retry here; a started billing mutation hands
                // recovery to ChoosePlan to resume the existing subscription.
                const subscriptionResult = await createSubscription(aspNetUserId, ac, paymentMethod.id, expectedQuote);
                if (!subscriptionResult.success) return;

                setProgressMessage("Success!");
                // -
                // ProgressPanel component handles the redirection upon "Success!"
                // -
            } catch (err) {
                console.error("Unexpected error:", err);
                setProgressMessage("Something went wrong. Please try again.");
                setProgressSteps((prev) => [prev[0], prev[1], prev[2], {percent: 100, status: "error"}]);
            }
        },

        handleBlur = (inputName: FormKey) => {
            const validate = validationFunctions[inputName];
            if (validate) {
                const validationResult = validate((formData[inputName] as string) ?? "", t);
                setFormError(setErrors, inputName, validationResult);

                if (inputName === "Email" && validationResult === "valid") {
                    const ac = abortRef.current ?? getAbort();
                    EmailFreeViewAPI(ac, formData.Email);
                }
            }
        },

        handleCloseProgressPopUp = () => {
            if (
                canResumeBilling &&
                authenticatedUserIdRef.current &&
                billingAttemptedRef.current
            ) {
                window.location.assign(withPromotionCode(RoutePaths.ChoosePlan));
                return;
            }

            setProgressPopUpActive(false);
            setIsLoading(false);
            setProgressMessage("");
            setProgressSteps(createInitialSignupProgressSteps());
            setCanResumeBilling(false);
        };

    useEffect(() => {
        SetTitle("Create an account");
        emailInputRef.current?.focus();
        return () => {
            abortRef.current?.abort();
        };
    }, []);

    return (
        <div
            className={`page page--sign-up page--no-nav ${globalInterfaceContext?.isDarkMode ? "is-dark-mode" : "is-light-mode"}`}
        >
            <div className={`page__inner ${isLoading ? "is-loading" : "is-not-loading"}`}>
                <div className="page__header">
                    <NavLink to="/">{IckonicLogo()}</NavLink>
                </div>

                <form className="form--sign-up" onSubmit={handleSubmit}>
                    <h1>{t("sign-up-heading")}</h1>

                    <h2>
                        <span>{t("sign-up-sub-heading-line-one")} </span>
                        <span>{t("sign-up-sub-heading-line-two")}</span>
                    </h2>

                    <p className="sign-up__login-switch">
                        Already have an account? <NavLink to={RoutePaths.Login}>Login</NavLink>
                    </p>

                    {/* Email */}
                    <InputField
                        type="email"
                        name="Email"
                        value={formData.Email}
                        label={t("form-label-email")}
                        onChange={handleChange}
                        onFocus={() => {
                        }}
                        onBlur={() => handleBlur("Email")}
                        hasError={errors.Email !== "valid"}
                        required
                        ref={emailInputRef}
                        errorMessage={errors.Email}
                        disabled={isLoading}
                    />

                    {/* First Name */}
                    <InputField
                        type="text"
                        name="FirstName"
                        value={formData.FirstName}
                        label={t("form-label-name-first")}
                        onChange={handleChange}
                        className="form__name-first"
                        onFocus={() => {
                        }}
                        onBlur={() => handleBlur("FirstName")}
                        required
                        hasError={errors.FirstName !== "valid"}
                        errorMessage={errors.FirstName}
                        disabled={isLoading}
                    />

                    {/* Last Name */}
                    <InputField
                        type="text"
                        name="LastName"
                        value={formData.LastName}
                        label={t("form-label-name-last")}
                        onChange={handleChange}
                        className="form__name-last"
                        onFocus={() => {
                        }}
                        onBlur={() => handleBlur("LastName")}
                        hasError={errors.LastName !== "valid"}
                        errorMessage={errors.LastName}
                        disabled={isLoading}
                    />

                    {/* Password */}
                    <div className="form__pw-wrapper">
                        <InputField
                            type="password"
                            name="Password"
                            value={formData.Password}
                            label="Password"
                            onChange={handleChange}
                            onFocus={() => {
                            }}
                            onBlur={() => handleBlur("Password")}
                            required
                            hasError={errors.Password !== "valid"}
                            errorMessage={errors.Password}
                            disabled={isLoading}
                        />
                    </div>

                    {/* Plan select */}
                    <h4 style={{margin: "1rem 0 2rem 0"}}>
                        <span>Select a plan</span>
                    </h4>

                    <PlansSelector
                        onSelectPlan={(plan) => handlePlanSelect(plan)}
                        parentSelectedPlan={selectedPlan}
                        hasError={errors.Plans !== "valid"}
                        errorMessage={errors.Plans}
                        isDarkMode={!!globalInterfaceContext?.isDarkMode}
                        featuresStart={2}
                        featuresEnd={5}
                        signupAffiliatePricing={signupAffiliatePricing}
                    />

                    <span className="form__popup-trigger"
                          onClick={() => setPlansPopUpActive(true)}>Expand all features</span>

                    <PopUp
                        isOpen={plansPopUpActive}
                        canBeDismissed={true}
                        isFullScreen={true}
                        onClose={() => setPlansPopUpActive(false)}
                        isDarkMode={!!globalInterfaceContext?.isDarkMode}
                    >
                        <PlansSelector
                            onSelectPlan={(plan) => {
                                handlePlanSelect(plan);
                                setPlansPopUpActive(false);
                            }}
                            parentSelectedPlan={selectedPlan}
                            hasError={errors.Plans !== "valid"}
                            errorMessage={errors.Plans}
                            isDarkMode={!!globalInterfaceContext?.isDarkMode}
                            featuresStart={0}
                            featuresEnd={10}
                            signupAffiliatePricing={plansPopUpActive ? signupAffiliatePricing : undefined}
                        />
                    </PopUp>

                    {/* Payment info tooltip */}
                    <h4 style={{margin: "2rem 0 1rem 0"}}>
                        <span><Tooltip
                            content={t("sign-up-payment-info-tooltip")}>Payment Information {IconQuestionFilled()}</Tooltip></span>
                    </h4>

                    {/* Stripe Credit Card */}
                    <InputsStripe
                        name="CreditCard"
                        value={formData.CreditCard}
                        onBlur={() => handleBlur("CreditCard")}
                        hasError={errors.CreditCard !== "valid"}
                        isDarkMode={!!globalInterfaceContext?.isDarkMode}
                        setCardComplete={setCardComplete}
                        updateZipCode={(newZip) => setZipCode(newZip)}
                    />

                    {/* ToS */}
                    <InputCheckbox
                        name="TermsOfServiceAccept"
                        checked={formData.TermsOfServiceAccept}
                        onChange={handleChange}
                        label={
                            <>
                                {t("sign-up-terms-of-service-consent")}{" "}
                                <NavLink to={RoutePaths.TermsOfUse}>{t("sign-up-terms-of-service-link-label")}</NavLink>
                            </>
                        }
                        required={true}
                    />

                    {/* Marketing consent */}
                    <InputCheckbox
                        name="AllowCommercialEmails"
                        checked={formData.AllowCommercialEmails}
                        onChange={handleChange}
                        label={t("sign-up-marketing-consent")}
                        required={false}
                    />

                    {selectedPlan && signupQuote.loading && (
                        <p role="status" style={{width: "100%"}}>Checking your price...</p>
                    )}
                    {selectedPlan && signupQuote.error && (
                        <div style={{width: "100%"}}>
                            <p role="alert">{signupQuote.error}</p>
                            <ButtonPill label="Retry price check" type="button" onClick={signupQuote.retry} disabled={isLoading} />
                            {!userAuthenticationContext.userData?.id && affiliateReferral && (
                                <ButtonPill label="Continue without affiliate discount" type="button" disabled={isLoading} onClick={() => {
                                    clearAffiliateReferral(true);
                                    setAffiliateReferral(null);
                                }} />
                            )}
                        </div>
                    )}

                    {/* Submit */}
                    <ButtonPill
                        label={isLoading
                            ? "Working…"
                            : getResolvedOffer(selectedPlan)?.cta_label?.trim() || "Join the family"}
                        type="submit"
                        disabled={isLoading || !signupQuote.quote || signupQuote.loading || !!signupQuote.error}
                    />
                </form>
            </div>

            {/* Progress Panel */}
            <ProgressPanel
                isOpen={progressPopUpActive}
                onClose={() => handleCloseProgressPopUp()}
                progressMessage={progressMessage}
                progressSteps={progressSteps}
                isDarkMode={!!globalInterfaceContext?.isDarkMode}
                errorActionLabel={canResumeBilling ? "continue to payment" : "close and try again"}
                onErrorAction={handleCloseProgressPopUp}
            />
        </div>
    );
};

export default SignUp;
